import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Typography,
  Row,
  Col,
  Tag,
  Avatar,
  Button,
  Modal,
  App,
} from 'antd';
import {
  StarOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

interface UserRating {
  id: number;
  brandId: number;
  brandName: string;
  brandLogo?: string;
  overallScore: number;
  dimensions: {
    environmental: number;
    laborRights: number;
    animalWelfare: number;
    transparency: number;
    socialResponsibility: number;
    carbonFootprint: number;
  };
  createdAt: string;
  updatedAt: string;
}

const dimensionNames = {
  environmental: '环保表现',
  laborRights: '劳工权益',
  animalWelfare: '动物福利',
  transparency: '透明度',
  socialResponsibility: '社会责任',
  carbonFootprint: '碳足迹',
};

const MyRatingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const [ratings, setRatings] = useState<UserRating[]>([]);
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthenticated) {
      message.error(t('myratings.login_required'));
      navigate('/login');
      return;
    }
    loadMyRatings();
  }, [isAuthenticated, navigate]);

  const loadMyRatings = async () => {
    setLoading(true);
    try {
      // 由于后端可能没有直接的用户评分历史API，我们需要通过其他方式获取
      // 这里先模拟数据结构，实际需要后端支持
      const mockRatings: UserRating[] = [
        {
          id: 1,
          brandId: 27,
          brandName: 'Armani',
          overallScore: 3.8,
          dimensions: {
            environmental: 4,
            laborRights: 4,
            animalWelfare: 3,
            transparency: 3,
            socialResponsibility: 5,
            carbonFootprint: 4,
          },
          createdAt: '2025-09-11T03:13:27.959Z',
          updatedAt: '2025-09-11T03:13:27.959Z',
        },
      ];
      setRatings(mockRatings);
    } catch (error) {
      message.error(t('myratings.loading_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRating = (ratingId: number) => {
    Modal.confirm({
      title: t('myratings.confirm_delete_title'),
      content: t('myratings.confirm_delete_content'),
      okText: t('myratings.confirm_ok'),
      okType: 'danger',
      cancelText: t('myratings.confirm_cancel'),
      onOk: async () => {
        try {
          // 这里需要后端API支持删除用户评分
          // await apiClient.deleteUserRating(ratingId);
          setRatings(ratings.filter(r => r.id !== ratingId));
          message.success(t('myratings.delete_success'));
        } catch (error) {
          message.error(t('myratings.delete_failed'));
        }
      },
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 4) return '#52c41a';
    if (score >= 3) return '#faad14';
    return '#ff4d4f';
  };

  const columns = [
    {
      title: t('myratings.col_brand'),
      dataIndex: 'brandName',
      key: 'brandName',
      render: (name: string, record: UserRating) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar
            src={record.brandLogo}
            size={40}
            style={{ backgroundColor: '#f0f0f0' }}
          >
            {name.charAt(0)}
          </Avatar>
          <div>
            <div
              style={{ fontWeight: 'bold', cursor: 'pointer' }}
              onClick={() => navigate(`/brands/${record.brandId}`)}
            >
              {name}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('myratings.col_overall'),
      dataIndex: 'overallScore',
      key: 'overallScore',
      width: 120,
      render: (score: number) => (
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: getScoreColor(score),
            }}
          >
            {score.toFixed(1)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>/ 5.0</div>
        </div>
      ),
    },
    {
      title: t('myratings.col_dimensions'),
      key: 'dimensions',
      render: (record: UserRating) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {Object.entries(record.dimensions).map(([key, score]) => (
            <Tag key={key} color={getScoreColor(score)}>
              {dimensionNames[key as keyof typeof dimensionNames]}: {score}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: t('myratings.col_time'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => {
        return new Date(date).toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      title: t('myratings.col_actions'),
      key: 'actions',
      width: 150,
      render: (record: UserRating) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/brands/${record.brandId}`)}
            title={t('myratings.view_brand')}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/brands/${record.brandId}/rate`)}
            title={t('myratings.re_rate')}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteRating(record.id)}
            title={t('myratings.delete_rating')}
          />
        </div>
      ),
    },
  ];

  const getStatistics = () => {
    if (ratings.length === 0) return null;

    const avgScore =
      ratings.reduce((sum, r) => sum + r.overallScore, 0) / ratings.length;
    const highestRated = ratings.reduce((max, r) =>
      r.overallScore > max.overallScore ? r : max
    );
    const lowestRated = ratings.reduce((min, r) =>
      r.overallScore < min.overallScore ? r : min
    );

    return (
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: 'var(--app-primary-color)',
                }}
              >
                {ratings.length}
              </div>
              <div style={{ color: '#666' }}>已评分品牌</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: getScoreColor(avgScore),
                }}
              >
                {avgScore.toFixed(1)}
              </div>
              <div style={{ color: '#666' }}>平均评分</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#52c41a',
                }}
              >
                {highestRated.brandName}
              </div>
              <div style={{ color: '#666' }}>
                最高评分 ({highestRated.overallScore.toFixed(1)})
              </div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#ff4d4f',
                }}
              >
                {lowestRated.brandName}
              </div>
              <div style={{ color: '#666' }}>
                最低评分 ({lowestRated.overallScore.toFixed(1)})
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: '24px' }}>
        <StarOutlined style={{ marginRight: '8px' }} />
        {t('myratings.title')}
      </Title>

      {getStatistics()}

      <Card>
        <Table
          columns={columns}
          dataSource={ratings}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              t('myratings.pagination_total', {
                start: range[0],
                end: range[1],
                total,
              }),
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <StarOutlined
                  style={{
                    fontSize: '48px',
                    color: '#d9d9d9',
                    marginBottom: '16px',
                  }}
                />
                <div
                  style={{
                    fontSize: '16px',
                    color: '#666',
                    marginBottom: '8px',
                  }}
                >
                  {t('myratings.empty_title')}
                </div>
                <div style={{ color: '#999' }}>
                  {t('myratings.empty_go_to', { page: t('nav.brands') })}
                </div>
              </div>
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default MyRatingsPage;
